import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SiteNavbar from './SiteNavbar';
import Footer from '@/pages/home/components/Footer';
import ScrollToTop from '@/pages/home/components/ScrollToTop';
import { injectBreadcrumbSchema } from '@/utils/breadcrumbSchema';

interface PageLayoutProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
}

const BASE_URL = 'https://thoughtic.com';
const DEFAULT_OG_IMAGE = 'https://thoughtic.com/thoughtic-solutions.png';

export default function PageLayout({ children, title, description, keywords, ogImage }: PageLayoutProps) {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const today = new Date().toISOString().split('T')[0];
    const canonicalUrl = `${BASE_URL}${location.pathname.replace(/\/$/, '') || '/'}`;
    const pageTitle = title ? `${title} | Thoughtic Solutions` : 'Thoughtic Solutions | Custom Software Development & IT Staffing';
    const pageImage = ogImage || DEFAULT_OG_IMAGE;

    // Title
    document.title = pageTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const parts = selector.match(/\[([^\]]+)="([^"]+)"\]/);
        if (parts) el.setAttribute(parts[1], parts[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
      el.setAttribute('data-page-specific', 'true');
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        document.head.appendChild(el);
      }
      el.href = href;
    };

    // Description
    if (description) setMeta('meta[name="description"]', 'content', description);

    // Keywords
    if (keywords) setMeta('meta[name="keywords"]', 'content', keywords);

    // Canonical
    setLink('canonical', canonicalUrl);

    // Open Graph
    setMeta('meta[property="og:title"]', 'content', pageTitle);
    if (description) setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    setMeta('meta[property="og:image"]', 'content', pageImage);

    // Twitter
    setMeta('meta[name="twitter:title"]', 'content', pageTitle);
    if (description) setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', pageImage);

    // BreadcrumbList Schema
    injectBreadcrumbSchema(location.pathname);
  }, [location.pathname, title, description, keywords, ogImage]);

  return (
    <div className="min-h-screen bg-white">
      <SiteNavbar />
      <main>{children}</main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}